/**
 * 사실적(realistic) 사무기기 오브젝트 모델 v2 (렌더 전용).
 * 외부 GLB 없이 프리미티브를 다중 파트로 조립하고 실물 기기 톤의 PBR 머티리얼을 입힌다.
 * 물리 콜라이더와 파괴 파편은 여전히 단순 프리미티브를 쓰므로(ObjectSpawner/fracture)
 * 성능·안정성은 유지된다.
 *
 * draw call 예산(원칙 IV)을 위해 파트를 머티리얼별로 병합(mergeGeometries)해 오브젝트당
 * 머티리얼 수만큼의 메시로 줄인다.
 */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { BreakableObjectDef } from '../core/types';
import type { DebrisProfile } from './DebrisRenderer';
import { noiseNormalMap, noiseRoughnessMap } from './textures';
import {
  makeBrowserScreenTexture,
  makeCodeScreenTexture,
  makeMugLabelTexture,
  makePhoneScreenTexture,
  makeStickerSheet,
} from './screens';

// 공유 디테일 맵(미세 요철·러프니스 변화). 절차 fbm이라 무거워 lazy 메모이즈 —
// 모듈 import 시점이 아니라 첫 모델 빌드 때 1회 계산(테스트/노드 import 비용 0).
let _detailNormal: THREE.Texture | null = null;
let _microNormal: THREE.Texture | null = null;
let _roughVar: THREE.Texture | null = null;
const detailNormal = (): THREE.Texture => (_detailNormal ??= noiseNormalMap(256, 0.55, 3));
const microNormal = (): THREE.Texture => (_microNormal ??= noiseNormalMap(256, 0.3, 7));
const roughVar = (): THREE.Texture => (_roughVar ??= noiseRoughnessMap(256, 0.55, 0.2, 3));

// ---- 공유 PBR 머티리얼 (실물 기기 톤) ----
const DEVICE_MATS = {
  // 모니터·맥북 공용: 초슬림 다크 베젤/후면 플라스틱.
  bezel: new THREE.MeshStandardMaterial({ color: 0x0d0f12, roughness: 0.35, metalness: 0.2 }),
  // 발광 스크린(켜진 패널 + 유리 클리어코트). 모니터/맥북 리드 공용.
  screen: new THREE.MeshPhysicalMaterial({
    color: 0x05070d,
    emissive: 0x3b82f6,
    emissiveIntensity: 1.1,
    roughness: 0.2,
    metalness: 0.0,
    clearcoat: 1.0,
    clearcoatRoughness: 0.06,
  }),
  // 모니터 스탠드·로고 바(광택 알루미늄).
  standMetal: new THREE.MeshStandardMaterial({ color: 0xbfc4cb, roughness: 0.3, metalness: 0.9 }),
  // 맥북 유니바디(샌드블라스트 알루미늄).
  aluminum: new THREE.MeshStandardMaterial({ color: 0xc7cbd1, roughness: 0.42, metalness: 0.85 }),
  // 키보드 로우프로파일 바디.
  kbBody: new THREE.MeshStandardMaterial({ color: 0x14161a, roughness: 0.5, metalness: 0.15 }),
  // 키캡 톤 3단계(미세 변화용).
  keycapA: new THREE.MeshStandardMaterial({ color: 0x2b2f37, roughness: 0.68, metalness: 0.05 }),
  keycapB: new THREE.MeshStandardMaterial({ color: 0x23262d, roughness: 0.72, metalness: 0.05 }),
  keycapC: new THREE.MeshStandardMaterial({ color: 0x1b1e24, roughness: 0.75, metalness: 0.05 }),
  // 마우스 매끈한 곡면 쉘(약한 클리어코트).
  mouseShell: new THREE.MeshPhysicalMaterial({
    color: 0x1a1d22,
    roughness: 0.42,
    metalness: 0.05,
    clearcoat: 0.4,
    clearcoatRoughness: 0.25,
  }),
  // 버튼 seam·휠 슬롯(거의 검정 홈).
  seam: new THREE.MeshStandardMaterial({ color: 0x07080a, roughness: 0.6, metalness: 0.1 }),
  // 금속 스크롤 휠.
  wheel: new THREE.MeshStandardMaterial({ color: 0x9aa0a8, roughness: 0.35, metalness: 0.9 }),
  // 맥북 키보드 인셋(다크 웰) + 소형 키.
  macInset: new THREE.MeshStandardMaterial({ color: 0x101216, roughness: 0.55, metalness: 0.1 }),
  macKey: new THREE.MeshStandardMaterial({ color: 0x191c22, roughness: 0.7, metalness: 0.05 }),
  // 맥북 트랙패드(매끈한 유리 톤).
  trackpad: new THREE.MeshStandardMaterial({ color: 0xb9bec6, roughness: 0.3, metalness: 0.7 }),
  // 키보드 주황 액센트 키캡(ESC/백스페이스/엔터 등, 레퍼런스 톤).
  kbAccent: new THREE.MeshStandardMaterial({ color: 0xe8590c, roughness: 0.55, metalness: 0.05 }),
  // 모니터암·클램프(블랙 스틸).
  armSteel: new THREE.MeshStandardMaterial({ color: 0x1a1c20, roughness: 0.42, metalness: 0.7 }),
  // 머그 무광 블랙 세라믹(핸들/립/안쪽 — 라벨 랩과 톤 통일, 안쪽 벽 가시화용 DoubleSide).
  mugBlack: new THREE.MeshPhysicalMaterial({
    color: 0x1b1c1f,
    roughness: 0.5,
    metalness: 0.02,
    clearcoat: 0.3,
    clearcoatRoughness: 0.3,
    side: THREE.DoubleSide,
  }),
  // 머그 내부(오목한 파인 부분) — 완전 무광 + 아주 어둡게. 반사가 없어 '물/커피 표면'처럼
  // 보이지 않고, 깊은 그늘진 빈 컵 내부로 읽힌다. DoubleSide(안쪽 벽 가시화).
  mugInner: new THREE.MeshStandardMaterial({
    color: 0x0b0b0d,
    roughness: 1.0,
    metalness: 0.0,
    side: THREE.DoubleSide,
  }),
  // 펭귄 토이(옐로 글로시 플라스틱) + 크림 배 + 부리/발 오렌지 + 눈.
  penguinYellow: new THREE.MeshPhysicalMaterial({
    color: 0xf5c531,
    roughness: 0.32,
    metalness: 0.0,
    clearcoat: 0.7,
    clearcoatRoughness: 0.2,
  }),
  penguinCream: new THREE.MeshPhysicalMaterial({
    color: 0xffedb8,
    roughness: 0.35,
    metalness: 0.0,
    clearcoat: 0.5,
    clearcoatRoughness: 0.25,
  }),
  penguinBeak: new THREE.MeshStandardMaterial({ color: 0xe8862e, roughness: 0.4, metalness: 0.0 }),
  eye: new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.2, metalness: 0.1 }),
  eyeWhite: new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3, metalness: 0.0 }),
  // 스마트폰 유리+알루미늄 프레임(글로시 블랙).
  phoneBody: new THREE.MeshPhysicalMaterial({
    color: 0x101216,
    roughness: 0.25,
    metalness: 0.45,
    clearcoat: 0.8,
    clearcoatRoughness: 0.1,
  }),
  // 헤드폰 무광 블랙 플라스틱 + 러버 패드.
  hpPlastic: new THREE.MeshStandardMaterial({ color: 0x17181c, roughness: 0.6, metalness: 0.1 }),
  hpPad: new THREE.MeshStandardMaterial({ color: 0x101114, roughness: 0.85, metalness: 0.0 }),
};

// 표면 디테일 맵을 머티리얼에 부여(평평함 제거).
function applyDetail(m: THREE.MeshStandardMaterial, normal: THREE.Texture, scale: number, rough = true): void {
  m.normalMap = normal;
  m.normalScale = new THREE.Vector2(scale, scale);
  if (rough) m.roughnessMap = roughVar();
  m.needsUpdate = true;
}

// 공유 머티리얼에 디테일 맵을 1회 부여(첫 모델 빌드 때 — noise fbm을 그때 계산).
let _detailApplied = false;
function ensureDeviceDetail(): void {
  if (_detailApplied) return;
  _detailApplied = true;
  applyDetail(DEVICE_MATS.bezel, microNormal(), 0.25);
  applyDetail(DEVICE_MATS.kbBody, detailNormal(), 0.3);
  applyDetail(DEVICE_MATS.keycapA, microNormal(), 0.35);
  applyDetail(DEVICE_MATS.keycapB, microNormal(), 0.35);
  applyDetail(DEVICE_MATS.keycapC, microNormal(), 0.35);
  applyDetail(DEVICE_MATS.mouseShell, microNormal(), 0.2);
  applyDetail(DEVICE_MATS.aluminum, microNormal(), 0.25, false);
  applyDetail(DEVICE_MATS.standMetal, microNormal(), 0.2, false);
  applyDetail(DEVICE_MATS.hpPlastic, detailNormal(), 0.3);
  applyDetail(DEVICE_MATS.armSteel, microNormal(), 0.2, false);
}

// ---- 캔버스 텍스처 머티리얼 (lazy — 테스트/노드 환경에서 DOM을 만지지 않도록) ----

const lazyMats = new Map<string, THREE.Material>();
function lazyMat<T extends THREE.Material>(key: string, make: () => T): T {
  let m = lazyMats.get(key) as T | undefined;
  if (!m) {
    m = make();
    lazyMats.set(key, m);
  }
  return m;
}

/** 켜진 패널: emissiveMap으로 화면 내용을 발광시키고 유리 클리어코트를 얹는다. */
function emissiveScreenMat(key: string, tex: THREE.Texture, intensity = 1.0): THREE.MeshPhysicalMaterial {
  return lazyMat(key, () =>
    new THREE.MeshPhysicalMaterial({
      color: 0x000000,
      map: tex,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: intensity,
      roughness: 0.18,
      metalness: 0.0,
      clearcoat: 1.0,
      clearcoatRoughness: 0.06,
    }),
  );
}

/** 머그 외벽: 라벨 랩(블랙 매트 + 화이트 타이포). */
function mugBodyMat(): THREE.MeshPhysicalMaterial {
  return lazyMat('mugBody', () =>
    new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      map: makeMugLabelTexture(),
      roughness: 0.5,
      metalness: 0.02,
      clearcoat: 0.3,
      clearcoatRoughness: 0.3,
    }),
  );
}

/** 맥북 리드 스티커 데칼(투명 시트). */
function stickerMat(): THREE.MeshStandardMaterial {
  return lazyMat('stickers', () =>
    new THREE.MeshStandardMaterial({
      map: makeStickerSheet(),
      transparent: true,
      roughness: 0.5,
      metalness: 0.0,
    }),
  );
}

// 키캡 톤 순환용(결정적 패턴으로 미세 톤 변화).
const KEY_TONES = [DEVICE_MATS.keycapA, DEVICE_MATS.keycapB, DEVICE_MATS.keycapC];

const ceramicCache = new Map<number, THREE.MeshPhysicalMaterial>();
function ceramic(color: number): THREE.MeshPhysicalMaterial {
  let m = ceramicCache.get(color);
  if (!m) {
    m = new THREE.MeshPhysicalMaterial({
      color,
      roughness: 0.28,
      metalness: 0.02,
      clearcoat: 0.7,
      clearcoatRoughness: 0.15,
      side: THREE.DoubleSide, // 머그 안쪽 벽 가시화
    });
    ceramicCache.set(color, m);
  }
  return m;
}

interface Part {
  geom: THREE.BufferGeometry;
  mat: THREE.Material;
}

interface XF {
  x?: number; y?: number; z?: number;
  rx?: number; ry?: number; rz?: number;
  sx?: number; sy?: number; sz?: number;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();

/** 지오메트리에 로컬 변환을 베이크한 뒤 Part로 감싼다. */
function part(geom: THREE.BufferGeometry, mat: THREE.Material, xf: XF = {}): Part {
  // RoundedBoxGeometry는 non-indexed, Box/Sphere/Cylinder 등은 indexed —
  // mergeGeometries는 혼합을 거부하므로 전부 non-indexed로 정규화(모델이 작아 비용 미미).
  if (geom.index) geom = geom.toNonIndexed();
  _e.set(xf.rx ?? 0, xf.ry ?? 0, xf.rz ?? 0);
  _q.setFromEuler(_e);
  _p.set(xf.x ?? 0, xf.y ?? 0, xf.z ?? 0);
  _s.set(xf.sx ?? 1, xf.sy ?? 1, xf.sz ?? 1);
  _m.compose(_p, _q, _s);
  geom.applyMatrix4(_m);
  geom.deleteAttribute('uv2'); // 병합 호환(속성 통일)
  return { geom, mat };
}

/** 조립 완료된 파트 전체를 원점 기준으로 기울인다(타이핑 각도 등). */
function tiltParts(parts: Part[], rx: number): Part[] {
  const m = new THREE.Matrix4().makeRotationX(rx);
  for (const p of parts) p.geom.applyMatrix4(m);
  return parts;
}

/** 파트 전체를 Y축 기준으로 돌린다(맥북을 등지게 세우는 연출 등). */
function turnParts(parts: Part[], ry: number): Part[] {
  const m = new THREE.Matrix4().makeRotationY(ry);
  for (const p of parts) p.geom.applyMatrix4(m);
  return parts;
}

/** 파트 전체를 위로 올린다(스탠드 거치 등). */
function liftParts(parts: Part[], dy: number): Part[] {
  const m = new THREE.Matrix4().makeTranslation(0, dy, 0);
  for (const p of parts) p.geom.applyMatrix4(m);
  return parts;
}

/** 파트 전체를 균일 배율로 스케일한다(카탈로그 bbox와 시각 모델 동기화). */
function scaleParts(parts: Part[], k: number): Part[] {
  const m = new THREE.Matrix4().makeScale(k, k, k);
  for (const p of parts) p.geom.applyMatrix4(m);
  return parts;
}

/** 파트를 머티리얼별로 병합해 Group으로 조립. */
function assemble(parts: Part[]): THREE.Group {
  const byMat = new Map<THREE.Material, THREE.BufferGeometry[]>();
  for (const p of parts) {
    const arr = byMat.get(p.mat) ?? [];
    arr.push(p.geom);
    byMat.set(p.mat, arr);
  }
  const group = new THREE.Group();
  for (const [material, geoms] of byMat) {
    const merged = geoms.length === 1 ? geoms[0] : mergeGeometries(geoms, false);
    if (!merged) continue;
    const mesh = new THREE.Mesh(merged, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}

// ---- 오브젝트별 빌더 (bbox 대체로 준수) ----

/**
 * 모니터: 초슬림 베젤 + 화면 CanvasTexture 발광 패널 + 후면 벌지 + **모니터암**
 * (책상 뒤 클램프 → 라이저 포스트 → 관절 → 수평 암 → VESA 플레이트). bbox 1.1x0.66x0.12.
 * kind에 따라 VS Code 다크/밝은 웹앱 화면을 입힌다(듀얼 모니터 연출).
 */
function buildMonitor(kind: 'code' | 'browser'): Part[] {
  const parts: Part[] = [];
  const screenTex = kind === 'code' ? makeCodeScreenTexture() : makeBrowserScreenTexture();
  const screenMat = emissiveScreenMat(`monitor:${kind}`, screenTex, 1.0);
  // 패널을 암 위로 띄워(책상과 갭) 레퍼런스처럼 "매달린" 실루엣을 만든다. 클램프는 책상에
  // 고정된 채 리저 폴이 늘어나는 형태라 LIFT만 올리면 화면이 더 높이 뜬다. 배경 데스크의
  // 스탠드형 모니터(office.ts buildBackgroundDesks, 받침~화면 거리 ≈0.22m)와 비슷한 넥
  // 길이가 되도록 값을 잡았다(스크린 하단이 책상면 위 LIFT+0.092m).
  const LIFT = 0.13;
  // 초슬림 다크 베젤 패널(하단 친만 살짝 두꺼움).
  parts.push(part(new RoundedBoxGeometry(1.1, 0.62, 0.04, 3, 0.012), DEVICE_MATS.bezel, { y: LIFT + 0.02, z: 0.01 }));
  // 전면 대부분을 차지하는 화면(코드/브라우저 텍스처 발광).
  parts.push(part(new THREE.BoxGeometry(1.06, 0.56, 0.006), screenMat, { y: LIFT + 0.04, z: 0.032 }));
  // 뒷면 완만한 벌지(전장 수납부 느낌).
  parts.push(part(new THREE.SphereGeometry(0.5, 24, 16), DEVICE_MATS.bezel, { y: LIFT + 0.02, z: -0.018, sx: 0.82, sy: 0.44, sz: 0.1 }));
  // 하단 은색 로고 바.
  parts.push(part(new THREE.BoxGeometry(0.1, 0.014, 0.006), DEVICE_MATS.standMetal, { y: LIFT - 0.262, z: 0.032 }));
  // 모니터암(블랙 스틸): VESA 브래킷 → 수평 암 링크 → 관절 노브 → 라이저 폴 → 데스크 클램프.
  parts.push(part(new RoundedBoxGeometry(0.12, 0.12, 0.022, 2, 0.008), DEVICE_MATS.armSteel, { y: LIFT + 0.0, z: -0.075 }));
  parts.push(part(new RoundedBoxGeometry(0.042, 0.036, 0.15, 2, 0.01), DEVICE_MATS.armSteel, { y: LIFT - 0.025, z: -0.125 }));
  parts.push(part(new THREE.SphereGeometry(0.032, 16, 12), DEVICE_MATS.armSteel, { y: LIFT - 0.04, z: -0.185 }));
  // 라이저 폴: 클램프(고정, y=-0.325 하단)에서 관절까지 — LIFT에 맞춰 길이·위치를 유도해
  // LIFT를 올려도 폴이 끊기지 않고 계속 관절에 닿는다. 패널 아래 갭에서 실루엣이 보이도록 굵게.
  const POLE_BOTTOM = -0.325;
  const POLE_TOP = LIFT - 0.055;
  const poleHeight = POLE_TOP - POLE_BOTTOM;
  parts.push(part(new THREE.CylinderGeometry(0.028, 0.028, poleHeight, 16), DEVICE_MATS.armSteel, { y: (POLE_TOP + POLE_BOTTOM) / 2, z: -0.185 }));
  parts.push(part(new RoundedBoxGeometry(0.13, 0.045, 0.16, 2, 0.01), DEVICE_MATS.armSteel, { y: -0.308, z: -0.185 }));
  return parts;
}

/**
 * 키보드: 로우프로파일 다크 바디 + 5열 실제 배열 키캡(와이드 키 포함) + 주황 액센트 키
 * (ESC/백스페이스/엔터/우측 하단 모디) + 타이핑 각도. bbox 0.9x0.1x0.34.
 */
function buildKeyboard(): Part[] {
  const parts: Part[] = [];
  // 로우프로파일 다크 바디.
  parts.push(part(new RoundedBoxGeometry(0.88, 0.045, 0.33, 3, 0.014), DEVICE_MATS.kbBody, { y: -0.015 }));
  // 행별 키 폭 배열(u 단위, 1u = pitch). 백스페이스/탭/엔터/시프트/스페이스 와이드 키.
  const pitch = 0.06;
  const gap = 0.012;
  const rowLayouts: number[][] = [
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], // 펑션열
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2], // 숫자열 + 백스페이스
    [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5], // 탭 ~ 엔터
    [2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2], // 시프트열
    [1.5, 1, 1.5, 6, 1.5, 1, 1.5], // 하단 모디파이어 + 스페이스바
  ];
  // 주황 액센트 키(row:index) — ESC / 백스페이스 / 엔터 / 우측 하단 모디파이어.
  const accentKeys = new Set(['0:0', '1:12', '2:12', '4:6']);
  const startZ = -0.12;
  for (let r = 0; r < rowLayouts.length; r++) {
    const layout = rowLayouts[r];
    let cursor = -0.42; // 14u 폭의 좌측 시작점
    for (let i = 0; i < layout.length; i++) {
      const u = layout[i];
      const tone = accentKeys.has(`${r}:${i}`) ? DEVICE_MATS.kbAccent : KEY_TONES[(r * 5 + i * 7) % KEY_TONES.length];
      parts.push(
        part(new RoundedBoxGeometry(u * pitch - gap, 0.022, 0.048, 2, 0.005), tone, {
          x: cursor + (u * pitch) / 2,
          y: 0.013,
          z: startZ + r * pitch,
        }),
      );
      cursor += u * pitch;
    }
  }
  return tiltParts(parts, 0.04); // 타이핑 각도(뒷쪽 들림)
}

/** 마우스: MX Master 스타일 — 곡면 쉘 + 좌측 섬레스트/섬휠 + 센터 seam + 금속 휠. bbox 0.14x0.08x0.22. */
function buildMouse(): Part[] {
  const parts: Part[] = [];
  // 매끈한 곡면 바디(스케일 스피어) + 하부도 곡면 스피어로 채워 밑면이 둥글게(직사각형 밑판 제거).
  parts.push(part(new THREE.SphereGeometry(0.5, 28, 20), DEVICE_MATS.mouseShell, { sx: 0.14, sy: 0.08, sz: 0.22 }));
  parts.push(part(new THREE.SphereGeometry(0.5, 28, 20), DEVICE_MATS.mouseShell, { y: -0.008, sx: 0.135, sy: 0.06, sz: 0.205 }));
  // 좌측 섬레스트(MX 시그니처 돌출 곡면).
  parts.push(part(new THREE.SphereGeometry(0.05, 20, 14), DEVICE_MATS.mouseShell, { x: -0.055, y: -0.014, z: 0.02, sx: 0.7, sy: 0.5, sz: 1.3 }));
  // 좌우 버튼 seam(센터 분할선) — 곡률을 따라 2분할로 배치.
  parts.push(part(new THREE.BoxGeometry(0.0025, 0.01, 0.048), DEVICE_MATS.seam, { y: 0.0375, z: -0.0375, rx: -0.14 }));
  parts.push(part(new THREE.BoxGeometry(0.0025, 0.01, 0.048), DEVICE_MATS.seam, { y: 0.0235, z: -0.0825, rx: -0.45 }));
  // 휠 슬롯(다크 홈) + 금속 스크롤 휠.
  parts.push(part(new THREE.BoxGeometry(0.022, 0.02, 0.046), DEVICE_MATS.seam, { y: 0.024, z: -0.06 }));
  parts.push(part(new THREE.CylinderGeometry(0.014, 0.014, 0.012, 16), DEVICE_MATS.wheel, { y: 0.032, z: -0.06, rz: Math.PI / 2 }));
  // 섬휠 힌트(측면 소형 금속 휠, 축 z 방향).
  parts.push(part(new THREE.CylinderGeometry(0.009, 0.009, 0.008, 12), DEVICE_MATS.wheel, { x: -0.05, y: 0.012, z: -0.035, rx: Math.PI / 2 }));
  return parts;
}

/**
 * 머그: 블랙 매트 + "WORK. STRESS. SMASH." 라벨 랩(사무직 전반) + 빈 컵 내부 + 손잡이.
 * 내부는 커피/물 없이 무광 어두운 재질로만 채워 오목한 음영(그늘)만 보이게 한다.
 * 반지름 0.11 높이 0.24. 손잡이는 +x(우측), 라벨 텍스트는 +z(정면)를 본다.
 */
function buildMug(): Part[] {
  const parts: Part[] = [];
  // 외벽(살짝 테이퍼된 본체) — 라벨 CanvasTexture 랩.
  parts.push(part(new THREE.CylinderGeometry(0.108, 0.095, 0.226, 28, 1, false), mugBodyMat(), { y: 0.005 }));
  // 상단 라운드 립(외벽-안쪽 벽 연결).
  parts.push(part(new THREE.TorusGeometry(0.1, 0.008, 10, 28), DEVICE_MATS.mugBlack, { y: 0.118, rx: Math.PI / 2 }));
  // 안쪽 벽(무광 어두운 재질, DoubleSide) — 립 바로 아래에서 바닥까지 깊게 파여, 위에서
  // 내려다보면 오목한 hollow("파인 부분")가 그늘로 읽힌다(빈 머그, 물/커피 없음).
  // 아래로 갈수록 좁아져(0.096→0.078) 원근으로 깊이감·음영을 강조.
  parts.push(part(new THREE.CylinderGeometry(0.096, 0.078, 0.206, 28, 1, true), DEVICE_MATS.mugInner, { y: 0.012 }));
  // 안쪽 바닥(깊은 그늘) — 무광 최암부. 반사가 없어 물 표면처럼 보이지 않는다.
  parts.push(part(new THREE.CircleGeometry(0.078, 28), DEVICE_MATS.mugInner, { y: -0.089, rx: -Math.PI / 2 }));
  // 토러스 손잡이(바깥쪽으로 열린 호).
  parts.push(part(new THREE.TorusGeometry(0.062, 0.016, 12, 24, Math.PI * 1.2), DEVICE_MATS.mugBlack, { x: 0.105, y: 0.005, rz: -Math.PI * 0.6 }));
  // 바닥 림(푸트 링).
  parts.push(part(new THREE.CylinderGeometry(0.078, 0.075, 0.016, 24), DEVICE_MATS.mugBlack, { y: -0.112 }));
  return parts;
}

/**
 * 맥북: 알루미늄 유니바디 웨지 + 다크 키보드 인셋 + 트랙패드 + 100° 리드.
 * 알루미늄 라이저 스탠드에 거치하고 리드 뒷면에 스티커 데칼을 붙인 뒤, 레퍼런스처럼
 * **등(스티커 면)이 카메라를 보도록** 180° 돌린다. ×0.72 스케일로 카탈로그 배율 정합
 * (모니터보다 확실히 작게 — 레퍼런스의 노트북≪모니터). 하판 0.62x0.036x0.42.
 */
function buildMacbook(): Part[] {
  const parts: Part[] = [];
  // 얇은 웨지 하판(알루미늄 유니바디).
  parts.push(part(new RoundedBoxGeometry(0.86, 0.042, 0.58, 3, 0.013), DEVICE_MATS.aluminum, { y: 0 }));
  // 다크 키보드 인셋(힌지 쪽 -z).
  parts.push(part(new RoundedBoxGeometry(0.64, 0.008, 0.26, 1, 0.004), DEVICE_MATS.macInset, { y: 0.02, z: -0.1 }));
  // 소형 키 그리드 13x4 + 스페이스바.
  const cols = 13;
  const rows = 4;
  const startX = -((cols - 1) * 0.047) / 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      parts.push(
        part(new THREE.BoxGeometry(0.038, 0.008, 0.034), DEVICE_MATS.macKey, {
          x: startX + c * 0.047,
          y: 0.028,
          z: -0.196 + r * 0.044,
        }),
      );
    }
  }
  parts.push(part(new THREE.BoxGeometry(0.22, 0.008, 0.034), DEVICE_MATS.macKey, { y: 0.028, z: -0.02 }));
  // 큰 트랙패드(전면 +z).
  parts.push(part(new RoundedBoxGeometry(0.3, 0.006, 0.2, 1, 0.003), DEVICE_MATS.trackpad, { y: 0.021, z: 0.155 }));
  // 리드: 힌지 기준으로 ~100° 개방(수직에서 뒤로 ~11°). 알루미늄 뒤판 + 다크 베젤 + 발광 스크린.
  const LID_TILT = -0.19;
  const hingeY = 0.018;
  const hingeZ = -0.272;
  const lid = (geom: THREE.BufferGeometry, mat: THREE.Material, ly: number, lz: number): Part => {
    geom.translate(0, ly, lz); // 힌지 기준 로컬 오프셋 → 회전에 함께 실림
    return part(geom, mat, { rx: LID_TILT, y: hingeY, z: hingeZ });
  };
  parts.push(lid(new RoundedBoxGeometry(0.85, 0.55, 0.013, 3, 0.008), DEVICE_MATS.aluminum, 0.27, -0.004));
  parts.push(lid(new THREE.BoxGeometry(0.82, 0.52, 0.005), DEVICE_MATS.bezel, 0.27, 0.004));
  parts.push(lid(new THREE.BoxGeometry(0.79, 0.48, 0.004), DEVICE_MATS.screen, 0.285, 0.008));
  // 리드 뒷면 스티커 데칼(투명 시트, -z를 보도록 뒤집어 부착).
  const sticker = new THREE.PlaneGeometry(0.58, 0.36);
  sticker.rotateY(Math.PI);
  sticker.translate(0, 0.27, -0.0115);
  parts.push(part(sticker, stickerMat(), { rx: LID_TILT, y: hingeY, z: hingeZ }));

  // 알루미늄 라이저 스탠드: 본체를 살짝 들어 올리고(콜라이더 오차 최소화) 플랫폼+다리를 받친다.
  liftParts(parts, 0.02);
  parts.push(part(new RoundedBoxGeometry(0.6, 0.012, 0.48, 2, 0.005), DEVICE_MATS.aluminum, { y: -0.007 }));
  parts.push(part(new RoundedBoxGeometry(0.52, 0.012, 0.05, 2, 0.005), DEVICE_MATS.aluminum, { y: -0.019, z: -0.17 }));
  parts.push(part(new RoundedBoxGeometry(0.52, 0.012, 0.05, 2, 0.005), DEVICE_MATS.aluminum, { y: -0.019, z: 0.17 }));

  // 화면이 카메라(+z)를 보도록 회전(스티커가 보이던 180°에서 170° 되돌림 → 약 -10°,
  // 정면이 아닌 살짝 튼 자연스러운 3/4 뷰로 화면이 보인다).
  const YAW = Math.PI + (170 * Math.PI) / 180;
  return scaleParts(turnParts(parts, YAW), 0.72);
}

/**
 * 노란 펭귄 피규어(Tux 실루엣): 세로로 길쭉한 통통한 몸통 + 큰 크림 배 패널 +
 * 몸에 붙은 플리퍼 + 납작한 오리부리 + 흰자 있는 눈 + 오렌지 발. cylinder r0.09 h0.22 근사.
 */
function buildPenguin(): Part[] {
  const parts: Part[] = [];
  // 하체: 아래가 넓은 통통한 몸통(전신 옐로 유지).
  parts.push(part(new THREE.SphereGeometry(0.075, 24, 18), DEVICE_MATS.penguinYellow, { y: -0.04, sx: 1.05, sy: 1.0, sz: 0.95 }));
  // 목~어깨: 몸통과 머리를 잇는 중간 로브(물방울 실루엣).
  parts.push(part(new THREE.SphereGeometry(0.064, 20, 16), DEVICE_MATS.penguinYellow, { y: 0.0, sx: 0.95, sy: 1.0, sz: 0.9 }));
  // 머리: 작은 상단 로브 — 2단 실루엣으로 병아리가 아닌 Tux 느낌을 만든다.
  parts.push(part(new THREE.SphereGeometry(0.055, 20, 16), DEVICE_MATS.penguinYellow, { y: 0.052, z: 0.005, sy: 1.05, sz: 0.9 }));
  // 배: 턱밑에서 발까지 내려오는 큰 크림 패널(Tux 시그니처).
  parts.push(part(new THREE.SphereGeometry(0.064, 20, 16), DEVICE_MATS.penguinCream, { y: -0.045, z: 0.038, sx: 0.8, sy: 1.05, sz: 0.5 }));
  // 플리퍼: 몸통 옆에 붙은 아래로 처진 납작 날개.
  parts.push(part(new THREE.SphereGeometry(0.048, 16, 12), DEVICE_MATS.penguinYellow, { x: -0.075, y: -0.045, z: -0.005, sx: 0.28, sy: 0.95, sz: 0.5, rz: 0.25 }));
  parts.push(part(new THREE.SphereGeometry(0.048, 16, 12), DEVICE_MATS.penguinYellow, { x: 0.075, y: -0.045, z: -0.005, sx: 0.28, sy: 0.95, sz: 0.5, rz: -0.25 }));
  // 부리: 위아래로 납작한 오리부리(콘을 수평으로 눕히고 세로 스쿼시).
  parts.push(part(new THREE.ConeGeometry(0.021, 0.05, 12), DEVICE_MATS.penguinBeak, { y: 0.048, z: 0.062, rx: Math.PI / 2, sx: 1.2, sz: 0.45 }));
  // 눈: 흰자 패치 + 블랙 동공(Tux 얼굴).
  parts.push(part(new THREE.SphereGeometry(0.013, 12, 10), DEVICE_MATS.eyeWhite, { x: -0.019, y: 0.068, z: 0.043, sz: 0.5 }));
  parts.push(part(new THREE.SphereGeometry(0.013, 12, 10), DEVICE_MATS.eyeWhite, { x: 0.019, y: 0.068, z: 0.043, sz: 0.5 }));
  parts.push(part(new THREE.SphereGeometry(0.006, 10, 8), DEVICE_MATS.eye, { x: -0.018, y: 0.068, z: 0.052 }));
  parts.push(part(new THREE.SphereGeometry(0.006, 10, 8), DEVICE_MATS.eye, { x: 0.018, y: 0.068, z: 0.052 }));
  // 발: 앞으로 뻗은 납작 오렌지 물갈퀴(살짝 팔자).
  parts.push(part(new THREE.SphereGeometry(0.028, 12, 8), DEVICE_MATS.penguinBeak, { x: -0.04, y: -0.108, z: 0.04, sy: 0.32, sz: 1.5, ry: 0.25 }));
  parts.push(part(new THREE.SphereGeometry(0.028, 12, 8), DEVICE_MATS.penguinBeak, { x: 0.04, y: -0.108, z: 0.04, sy: 0.32, sz: 1.5, ry: -0.25 }));
  return parts;
}

/**
 * 스마트폰: 글로시 블랙 슬랩 + 잠금화면 텍스처(화면 위) + 측면 버튼.
 * 실물 치수로 조립 후 ×2 스케일(카탈로그 배율 정합 — 다른 오브젝트와 동일한 확대율).
 * bbox 0.15x0.03x0.31.
 */
function buildPhone(): Part[] {
  const parts: Part[] = [];
  const screenMat = emissiveScreenMat('phone', makePhoneScreenTexture(), 0.9);
  // 바디(라운드 슬랩, 화면이 위를 보도록 눕힘).
  parts.push(part(new RoundedBoxGeometry(0.075, 0.013, 0.155, 3, 0.006), DEVICE_MATS.phoneBody, { y: -0.001 }));
  // 화면(얇은 슬랩의 윗면에 잠금화면 텍스처).
  parts.push(part(new THREE.BoxGeometry(0.066, 0.0024, 0.144), screenMat, { y: 0.0062 }));
  // 측면 전원 버튼.
  parts.push(part(new THREE.BoxGeometry(0.002, 0.004, 0.018), DEVICE_MATS.standMetal, { x: 0.0375, z: -0.03 }));
  return scaleParts(parts, 2.0);
}

/**
 * 헤드폰: 오버이어 밴드(토러스 호) + 요크 + 이어컵 + 러버 패드 + 실버 캡.
 * 실물 치수로 조립 후 ×1.7 스케일(카탈로그 배율 정합). bbox 0.34x0.37x0.17 근사.
 */
function buildHeadphones(): Part[] {
  const parts: Part[] = [];
  // 헤드밴드(상부 반원 호).
  parts.push(part(new THREE.TorusGeometry(0.088, 0.013, 12, 32, Math.PI), DEVICE_MATS.hpPlastic, { y: 0.005 }));
  // 밴드 안쪽 쿠션(러버).
  parts.push(part(new THREE.TorusGeometry(0.072, 0.009, 8, 24, Math.PI * 0.7), DEVICE_MATS.hpPad, { y: 0.005, rz: Math.PI * 0.15 }));
  // 좌우 요크(밴드 끝 → 이어컵 연결).
  parts.push(part(new THREE.BoxGeometry(0.012, 0.05, 0.012), DEVICE_MATS.hpPlastic, { x: -0.088, y: -0.018 }));
  parts.push(part(new THREE.BoxGeometry(0.012, 0.05, 0.012), DEVICE_MATS.hpPlastic, { x: 0.088, y: -0.018 }));
  // 이어컵(축이 x를 향하는 원통).
  parts.push(part(new THREE.CylinderGeometry(0.052, 0.052, 0.038, 24), DEVICE_MATS.hpPlastic, { x: -0.078, y: -0.055, rz: Math.PI / 2 }));
  parts.push(part(new THREE.CylinderGeometry(0.052, 0.052, 0.038, 24), DEVICE_MATS.hpPlastic, { x: 0.078, y: -0.055, rz: Math.PI / 2 }));
  // 이어 패드(안쪽 러버 링).
  parts.push(part(new THREE.TorusGeometry(0.036, 0.014, 10, 20), DEVICE_MATS.hpPad, { x: -0.058, y: -0.055, ry: Math.PI / 2 }));
  parts.push(part(new THREE.TorusGeometry(0.036, 0.014, 10, 20), DEVICE_MATS.hpPad, { x: 0.058, y: -0.055, ry: Math.PI / 2 }));
  // 외측 실버 캡(액센트).
  parts.push(part(new THREE.CylinderGeometry(0.042, 0.042, 0.006, 20), DEVICE_MATS.standMetal, { x: -0.099, y: -0.055, rz: Math.PI / 2 }));
  parts.push(part(new THREE.CylinderGeometry(0.042, 0.042, 0.006, 20), DEVICE_MATS.standMetal, { x: 0.099, y: -0.055, rz: Math.PI / 2 }));
  return scaleParts(parts, 1.7);
}

// ---- 파편 팔레트 (오브젝트 실제 재질을 반영) ----

/** 파편 재질 프로파일 + 틴트 색 + 가중치. */
export interface PaletteEntry {
  profile: DebrisProfile;
  color: number;
  weight: number;
}

const DEBRIS_PALETTES: Record<string, PaletteEntry[]> = {
  monitor: [
    { profile: 'plasticDark', color: 0x0d0f12, weight: 4 },
    { profile: 'screen', color: 0x3b82f6, weight: 2 },
    { profile: 'metal', color: 0xbfc4cb, weight: 1 },
  ],
  keyboard: [
    { profile: 'plasticDark', color: 0x14161a, weight: 3 },
    { profile: 'keycap', color: 0x2b2f37, weight: 3 },
    { profile: 'keycap', color: 0x1b1e24, weight: 2 },
  ],
  mouse: [
    { profile: 'plasticDark', color: 0x1a1d22, weight: 5 },
    { profile: 'metal', color: 0x9aa0a8, weight: 1 },
  ],
  macbook: [
    { profile: 'metal', color: 0xc7cbd1, weight: 5 },
    { profile: 'screen', color: 0x3b82f6, weight: 2 },
    { profile: 'keycap', color: 0x191c22, weight: 2 },
    { profile: 'plasticDark', color: 0x101216, weight: 1 },
  ],
  // 펭귄: 옐로/크림 글로시 플라스틱 + 부리 오렌지 약간.
  penguin: [
    { profile: 'ceramic', color: 0xf5c531, weight: 5 },
    { profile: 'ceramic', color: 0xffedb8, weight: 2 },
    { profile: 'ceramic', color: 0xe8862e, weight: 1 },
  ],
  // 폰: 블랙 바디 + 발광 유리 화면 + 프레임 메탈 약간.
  phone: [
    { profile: 'plasticDark', color: 0x101216, weight: 4 },
    { profile: 'screen', color: 0x8fb0ff, weight: 3 },
    { profile: 'metal', color: 0x2a2d33, weight: 1 },
  ],
  // 헤드폰: 무광 블랙 + 실버 캡 + 러버 패드.
  headphones: [
    { profile: 'plasticDark', color: 0x17181c, weight: 5 },
    { profile: 'metal', color: 0xbfc4cb, weight: 1 },
    { profile: 'rubber', color: 0x101114, weight: 1 },
  ],
};

// 좌측 VS Code 모니터(다크 화면) 전용: 채도 높은 파랑 대신 다크 에디터 패널 톤 위주 +
// 발광 잔광은 소량만 남겨 원본 화면과 파편의 색 연속성을 지킨다.
const MONITOR_CODE_PALETTE: PaletteEntry[] = [
  { profile: 'plasticDark', color: 0x0d0f12, weight: 4 },
  { profile: 'plasticDark', color: 0x22262e, weight: 2 },
  { profile: 'screen', color: 0x2e3440, weight: 1 },
  { profile: 'metal', color: 0xbfc4cb, weight: 1 },
];

/**
 * 오브젝트 파편 팔레트. mug는 def.color(세라믹 색)를 반영, 미지정은 def.color 세라믹 폴백.
 * variant: 같은 def를 공유하는 시각 변형 구분(모니터 'code'/'browser' — userData.variant).
 */
export function debrisPaletteFor(def: BreakableObjectDef, variant?: string): PaletteEntry[] {
  if (def.id === 'monitor' && variant === 'code') return MONITOR_CODE_PALETTE;
  if (def.id === 'mug') {
    return [
      { profile: 'ceramic', color: def.color, weight: 6 },
      { profile: 'ceramic', color: 0x24140c, weight: 1 },
    ];
  }
  return DEBRIS_PALETTES[def.id] ?? [{ profile: 'ceramic', color: def.color, weight: 1 }];
}

/** 가중치 기반 팔레트 샘플. */
export function pickPaletteEntry(palette: PaletteEntry[], rng: () => number = Math.random): PaletteEntry {
  const total = palette.reduce((s, e) => s + e.weight, 0);
  let r = rng() * total;
  for (const e of palette) {
    r -= e.weight;
    if (r <= 0) return e;
  }
  return palette[palette.length - 1];
}

// 모니터 빌드 카운터 — 빌드마다 코드/브라우저 화면을 번갈아 적용(듀얼 모니터 연출).
let monitorCycle = 0;

/**
 * 병합 지오메트리 캐시: 키보드(키캡 ~66개)·맥북(파트 110여 개) 조립은 수십 ms급이라
 * 스폰/리셋마다 재빌드하면 메인스레드 히치가 난다. def.id(모니터는 화면 kind 포함) 키로
 * 프로토타입을 1회 조립하고 clone()으로 공유한다 — clone은 지오메트리·머티리얼을 공유하므로
 * ObjectSpawner.resetAll은 지오메트리를 dispose하면 안 된다.
 */
const modelCache = new Map<string, THREE.Group>();

function buildParts(def: BreakableObjectDef, monitorKind: 'code' | 'browser'): Part[] {
  switch (def.id) {
    case 'monitor': return buildMonitor(monitorKind);
    case 'keyboard': return buildKeyboard();
    case 'mouse': return buildMouse();
    case 'mug': return buildMug();
    case 'macbook': return buildMacbook();
    case 'penguin': return buildPenguin();
    case 'phone': return buildPhone();
    case 'headphones': return buildHeadphones();
    default:
      return [part(new THREE.BoxGeometry(0.2, 0.2, 0.2), ceramic(def.color))];
  }
}

/** def.id로 상세 모델 Group을 만든다(캐시 clone). 미지원 id는 프리미티브 박스 폴백. */
export function buildObjectModel(def: BreakableObjectDef): THREE.Group {
  ensureDeviceDetail(); // 공유 머티리얼 디테일 맵 1회 부여(첫 빌드 때 noise 계산).
  // 모니터는 스폰 순서대로 코드/브라우저를 번갈아 — kind가 캐시 키와 userData.variant에 실린다.
  const monitorKind = def.id === 'monitor' ? (monitorCycle++ % 2 === 0 ? 'code' : 'browser') : 'code';
  const key = def.id === 'monitor' ? `monitor:${monitorKind}` : def.id;
  let proto = modelCache.get(key);
  if (!proto) {
    proto = assemble(buildParts(def, monitorKind));
    // 파편 팔레트 분기용 시각 변형 태그(clone 시 userData가 깊은 복사되어 함께 전달됨).
    if (def.id === 'monitor') proto.userData.variant = monitorKind;
    modelCache.set(key, proto);
  }
  return proto.clone(true);
}
